import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import axios from 'axios'
import queryString from 'query-string'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import {
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  ESSENTIALS_IS_INITIAL_LOAD_PARAM,
  ESSENTIALS_ORIENTATION_MIXES_MODAL,
  MIXPANEL_EVENT_ATTRIBUTES,
  PAGE_ROUTES,
  TOOLKIT_TYPE,
  TRIAGE_ENTRY_POINTS,
  useFlags,
  WELLNESS_TOOLKIT_EVENTS_PROPERTIES,
  WORK_HUB_EVENT_DEREGISTRATION_RETRY_MESSAGE,
  WORK_HUB_EVENT_REGISTRATION_LIMIT_REACHED_MESSAGE,
  WORK_HUB_EVENT_REGISTRATION_RETRY_MESSAGE,
} from '@lyrahealth-inc/shared-app-logic'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core'
import { getPromotionTitle, toJS, useMediaQuerySize } from '@lyrahealth-inc/ui-core-crossplatform'

import styles from './essentials.module.scss'
import { ESSENTIALS_IFRAME_MESSAGES, ESSENTIALS_MODAL, LYRA_HEALTH_NAME } from '../../common/constants/appConstants'
import { REDIRECT_URL_KEY } from '../../common/constants/routingConstants'
import { useDefaultMixpanelEntryPoint } from '../../common/hooks/useDefaultMixpanelEntryPoint'
import { useNavigateToForkedTriage } from '../../common/hooks/useNavigateToForkedTriage'
import { useNavigateToSearchForCare } from '../../common/hooks/useNavigateToSearchForCare'
import { useTrackViewPage } from '../../common/hooks/useTrackViewPage'
import { axiosInstance } from '../../common/http/axiosInstance'
import { getCourseCompletionCertificate } from '../../common/http/data/libraryCourseCertificate'
import { getDeviceUUID } from '../../data/appGlobals/appGlobalsSelectors'
import { setTimeInactivityWarning, setTimeLastActive } from '../../data/auth/authActions'
import { setShouldGoFullscreenForMixes } from '../../data/essentials/essentialsActions'
import { trackEventWithObj } from '../../data/mixpanel'
import { getId, getIsUserLoggedIn, getUserDisplayLanguageOrDefault } from '../../data/user/userSelectors'
import { getOnboardToolkitType } from '../onboard/data/onboardSelectors'
import { setTriageEntryPoint } from '../register/data/registerActions'
type EssentialsProps = {
  lyraId: string
  userDisplayLanguage: string
  deviceUUID: string
  toolkitType?: TOOLKIT_TYPE
  isLoggedIn: boolean
  setTimeInactivityWarning: (value: number | null) => void
  setTimeLastActive: (value: number) => void
  setTriageEntryPoint: (entryPoint: TRIAGE_ENTRY_POINTS, redirectUrlOnTriageExit?: string) => void
  setShouldGoFullscreenForMixes: (value: boolean) => void
}

// Ensure the entryPoint is included in the URL when navigating
// to an essential page, so Essentials.tsx can parse it for Mixpanel.
// Use the title of the clicked component as the entryPoint
// (e.g., for a CTA titled "explore essential," use entryPoint="explore essential").

const Essentials: React.FC<EssentialsProps> = ({
  lyraId,
  userDisplayLanguage,
  deviceUUID,
  toolkitType,
  isLoggedIn,
  setTimeInactivityWarning,
  setTimeLastActive,
  setTriageEntryPoint,
  setShouldGoFullscreenForMixes,
}) => {
  const [iFrameHeight, setIFrameHeight] = useState<number>(0)
  const [isEssentialsLoading, setIsEssentialsLoading] = useState(true)
  const [iframeUrl, setIframeUrl] = useState<string>('')
  const [contentPath, setContentPath] = useState<string>('')
  const modalOpenRef = useRef<boolean>(false)
  const essentialsRef = useRef<HTMLIFrameElement>(null)
  const intervalRef = useRef(0)
  const location = useLocation()
  const { pathname, state } = location
  const referrerPage = useRef<string>(state?.[COMMON_MIXPANEL_PROPERTIES.REFERRER_PAGE])
  const intl = useIntl()
  const headerHeight = 80
  const minIFrameHeight = 680
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const parentAppReferrer = searchParams.get('parentAppReferrer')
  const mixpanelEntryPoint = useDefaultMixpanelEntryPoint(MIXPANEL_EVENT_ATTRIBUTES.ENTRY_POINT.DIRECT_LINK)
  const localeOverride = searchParams.get('locale')
  const { isMobileSized } = useMediaQuerySize()
  const { essentialsInitialLoadParamEnabled: isInitialLoadParamEnabled, isUngatedEssentialsEventsEnabled } = useFlags()
  const navigateToForkedTriage = useNavigateToForkedTriage()
  const navigateToSearchForCare = useNavigateToSearchForCare()
  const toolkitTopicTitle = toolkitType ? getPromotionTitle(toolkitType) : undefined
  const isUngatedArticles = pathname.startsWith(PAGE_ROUTES.ESSENTIALS_ARTICLES)
  const isUngatedEvents = pathname.startsWith(PAGE_ROUTES.ESSENTIALS_EVENTS) && isUngatedEssentialsEventsEnabled
  const isUngatedContent = !isLoggedIn && (isUngatedArticles || isUngatedEvents)

  let essentialsUrl = `${window.location.origin}${contentPath}`
  const essentialsUrlObj = new URL(essentialsUrl)
  essentialsUrlObj.searchParams.set(
    'mixpanelProps',
    JSON.stringify({
      [COMMON_MIXPANEL_PROPERTIES.LD_DEVICE_UUID]: deviceUUID,
    }),
  )
  if (isInitialLoadParamEnabled) {
    essentialsUrlObj.searchParams.set(ESSENTIALS_IS_INITIAL_LOAD_PARAM, 'true')
  }
  if (searchParams.get('referrer')) {
    essentialsUrlObj.searchParams.set('referrer', searchParams.get('referrer')!)
  }
  if (searchParams.get('toggleTranscript')) {
    essentialsUrlObj.searchParams.set('toggleTranscript', searchParams.get('toggleTranscript')!)
  }
  if (searchParams.get(ESSENTIALS_ORIENTATION_MIXES_MODAL)) {
    essentialsUrlObj.searchParams.set(
      ESSENTIALS_ORIENTATION_MIXES_MODAL,
      searchParams.get(ESSENTIALS_ORIENTATION_MIXES_MODAL)!,
    )
  }
  if (searchParams.get('mixpanelEventsAddon')) {
    essentialsUrlObj.searchParams.set('mixpanelEventsAddon', searchParams.get('mixpanelEventsAddon')!)
  }
  essentialsUrl = essentialsUrlObj.toString()
  const essentialsTitle = intl.formatMessage({
    defaultMessage: 'Library',
    description: 'Browser tab title text when visiting the Essentials and Work Hub home page',
  })
  document.title = `${essentialsTitle} | ${LYRA_HEALTH_NAME}`

  useTrackViewPage(
    COMMON_MIXPANEL_PAGE.ESSENTIALS,
    {
      referrer_page: referrerPage.current ?? COMMON_MIXPANEL_PAGE.NONE,
      ...(toolkitTopicTitle && {
        [WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC]: toolkitTopicTitle,
      }),
    },
    { skipAppendPageToEventName: true, entryPoint: mixpanelEntryPoint },
  )

  const onIFrameLoad = useCallback(() => {
    setIsEssentialsLoading(false)
    if (essentialsRef?.current) {
      if (essentialsRef.current.contentWindow) {
        setIframeUrl(essentialsRef.current.contentWindow?.document.URL)
        if (modalOpenRef.current === false) {
          setIFrameHeight(essentialsRef?.current?.contentWindow.document?.body.scrollHeight)
        }

        // Watch iframe url so we can scroll to the top on page change.
        // Also watch the iframe content height to set the height of the iframe on page change.
        intervalRef.current = window.setInterval(() => {
          setIframeUrl(essentialsRef?.current?.contentWindow?.document.URL as string)
          if (modalOpenRef.current === false && essentialsRef?.current?.contentWindow) {
            setIFrameHeight(essentialsRef?.current?.contentWindow.document?.body.scrollHeight)
          }
        }, 500)
      }
    }
  }, [modalOpenRef])

  useEffect(() => {
    if (iframeUrl) {
      scroll(0, 0)
    }
  }, [iframeUrl])

  useEffect(() => {
    window.addEventListener('message', handleMessage, false)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // clear entryPoint params after use
  useEffect(() => {
    const params = {
      parentAppReferrer,
      locale: localeOverride !== userDisplayLanguage ? localeOverride : null,
    }
    const query = queryString.stringify(params, { skipNull: true, encode: false })
    const redirectUrl = location.pathname + (query.length > 0 ? `?${query}` : '')

    window.history.replaceState(null, '', redirectUrl)
  }, [location, parentAppReferrer, localeOverride, userDisplayLanguage])

  // Set the content pathname when the iframe is loaded
  // Add the query param to indicate un-gated content if necessary
  useEffect(() => {
    const pathname = window.location.pathname
    const search = window.location.search
    const hash = window.location.hash

    // If this is a valid request for preview content, add the preview search param and update the content path
    if (isUngatedContent) {
      const receivedSearchParams = queryString.parse(search)
      receivedSearchParams.preview = 'true'
      const updatedSearch = `?${queryString.stringify(receivedSearchParams)}`
      setContentPath(`/secure${pathname}${updatedSearch}${hash}`)
    } else if (pathname.startsWith('/essentials/') && !isUngatedContent) {
      // Non-articles or non-events /essentials/* paths - redirect to proper secure route
      const essentialsSubPath = pathname.replace('/essentials', '')
      if (lyraId) {
        // Logged-in users redirect to /secure/index/essentials/*
        window.location.replace(`/secure/index/essentials${essentialsSubPath}${search}${hash}`)
      } else {
        // Logged-out users redirect to login
        window.location.replace(PAGE_ROUTES.LOGIN)
      }
    } else {
      setContentPath(`${pathname.replace('/index', '')}${search}${hash}`)
    }
  }, [isUngatedContent, lyraId, setContentPath])

  const handleMessage = (message: MessageEvent) => {
    if (message.origin === window.location.origin) {
      switch (message.data.event) {
        case ESSENTIALS_IFRAME_MESSAGES.MODAL_CLOSED:
          // helps exit Mixes views out of fullscreen mode by showing header and footer
          if (
            message.data?.modalName === ESSENTIALS_MODAL.SEARCH_FOR_CARE ||
            message.data?.modalName === ESSENTIALS_MODAL.ORIENTATION_MIXES
          ) {
            setShouldGoFullscreenForMixes(false)
          }
          modalOpenRef.current = false
          break
        case ESSENTIALS_IFRAME_MESSAGES.MODAL_OPENED:
          let applicationContentHeight = window.innerHeight - headerHeight
          // helps display Mixes views in fullscreen mode by hiding header and footer
          if (
            message.data?.modalName === ESSENTIALS_MODAL.MIXES_PLAYER ||
            message.data?.modalName === ESSENTIALS_MODAL.SEARCH_FOR_CARE ||
            message.data?.modalName === ESSENTIALS_MODAL.ORIENTATION_MIXES
          ) {
            setShouldGoFullscreenForMixes(true)
            // do not subtract header height from iframe height in fullscreen mode
            applicationContentHeight = window.innerHeight
          }

          // The iframe should have a minimum height of 680px when a modal is open
          const iFrameHeightForModal =
            applicationContentHeight < minIFrameHeight && !isMobileSized ? minIFrameHeight : applicationContentHeight
          modalOpenRef.current = true
          setIFrameHeight(iFrameHeightForModal)
          essentialsRef?.current?.contentWindow?.focus()
          break
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_PARENT_APP_ROUTE:
          navigate(-1)
          break
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_FIND_PROVIDER:
          setShouldGoFullscreenForMixes(false)
          setTriageEntryPoint(TRIAGE_ENTRY_POINTS.ESSENTIALS, window.location.pathname)
          navigateToSearchForCare()
          break
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_SEARCH_FOR_CARE:
          setShouldGoFullscreenForMixes(false)
          setTriageEntryPoint(TRIAGE_ENTRY_POINTS.ESSENTIALS, window.location.pathname)
          navigateToForkedTriage()
          break
        case ESSENTIALS_IFRAME_MESSAGES.ROUTE_CHANGE:
          handleRouteChange(message)
          break
        case ESSENTIALS_IFRAME_MESSAGES.CONTENT_PLAYING:
          setTimeInactivityWarning(null)
          setTimeLastActive(Date.now())
          break
        case ESSENTIALS_IFRAME_MESSAGES.REGISTER_FOR_EVENT:
          handleRegisterForEvent(message)
          break
        case ESSENTIALS_IFRAME_MESSAGES.DEREGISTER_FOR_EVENT:
          handleDeregisterForEvent(message)
          break
        case ESSENTIALS_IFRAME_MESSAGES.EVENT_REGISTERED_COUNT:
          handleEventRegisteredCounts(message)
          break
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_BETWEEN_LIBRARY_APPS:
          handleNavigateBetweenLibraryApps(message)
          break
        case ESSENTIALS_IFRAME_MESSAGES.CREATE_COURSE_COMPLETION_CERTIFICATE:
          getCourseCompletionCertificate(lyraId, message.data.courseName, message.data.courseCompletionDate)
          break
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_COACHING_DIRECT_PATH:
          window.open(message.data.coachingUrl, '_self')
          break
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_LOGIN:
        case ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_REGISTER: {
          const navigateToLogin = message.data.event === ESSENTIALS_IFRAME_MESSAGES.NAVIGATE_TO_LOGIN

          // Save redirect URL for ungated Essentials content before navigating to login/register
          // so user is redirected back to the article after authentication
          if (isUngatedContent) {
            // Convert ungated path to secure path for post-login redirect
            const { pathname, search, hash } = window.location
            const fullRedirectUrl = `/secure/index${pathname}${search}${hash}`
            const redirectUrlKey = navigateToLogin ? REDIRECT_URL_KEY : UNAUTHENTICATED_REDIRECT_URL
            // REDIRECT_URL_KEY uses JSON.stringify for consistency with other parts of the codebase
            // UNAUTHENTICATED_REDIRECT_URL stores the string directly
            const redirectValue =
              redirectUrlKey === REDIRECT_URL_KEY ? JSON.stringify(fullRedirectUrl) : fullRedirectUrl
            window.sessionStorage.setItem(redirectUrlKey, redirectValue)
          }
          const targetRoute = navigateToLogin ? PAGE_ROUTES.LOGIN : PAGE_ROUTES.REGISTER
          navigate(targetRoute)
          break
        }
      }
    }
  }

  const handleRegisterForEvent = async (message: MessageEvent) => {
    const data = {
      zoom_event_id: message.data.zoom_event_id,
      zoom_event_type: message.data.zoom_event_type,
      ticket_type_id: message.data.ticket_type_id,
      ...(message.data.event_category && { event_category: message.data.event_category }), // Set optional event_category
    }
    try {
      const response = await axiosInstance.post(`/v1/zoom-event/register/${lyraId}`, data)
      essentialsRef.current?.contentWindow?.postMessage(
        {
          ...data,
          ...response.data,
          event: ESSENTIALS_IFRAME_MESSAGES.REGISTER_FOR_EVENT,
          success: true,
        },
        essentialsRef.current.contentWindow.origin,
      )
    } catch (error) {
      let errorMessage = intl.formatMessage(WORK_HUB_EVENT_REGISTRATION_RETRY_MESSAGE)
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        errorMessage = intl.formatMessage(WORK_HUB_EVENT_REGISTRATION_LIMIT_REACHED_MESSAGE)
      }
      essentialsRef.current?.contentWindow?.postMessage(
        {
          ...data,
          event: ESSENTIALS_IFRAME_MESSAGES.REGISTER_FOR_EVENT,
          success: false,
          error_message: errorMessage,
        },
        essentialsRef.current.contentWindow.origin,
      )
    }
  }

  const handleDeregisterForEvent = async (message: MessageEvent) => {
    const data = {
      zoom_event_id: message.data.zoom_event_id,
      zoom_event_type: message.data.zoom_event_type,
      registrant_id: message.data.registrant_id,
      ...(message.data.event_category && { event_category: message.data.event_category }), // Set optional event_category
    }
    try {
      await axiosInstance.post(`/v1/zoom-event/unregister/${lyraId}`, data)
    } catch {
      essentialsRef.current?.contentWindow?.postMessage(
        {
          ...data,
          event: ESSENTIALS_IFRAME_MESSAGES.DEREGISTER_FOR_EVENT,
          success: false,
          error_message: intl.formatMessage(WORK_HUB_EVENT_DEREGISTRATION_RETRY_MESSAGE),
        },
        essentialsRef.current.contentWindow.origin,
      )
      return
    }
    essentialsRef.current?.contentWindow?.postMessage(
      {
        ...data,
        event: ESSENTIALS_IFRAME_MESSAGES.DEREGISTER_FOR_EVENT,
        success: true,
      },
      essentialsRef.current.contentWindow.origin,
    )
  }
  const handleEventRegisteredCounts = async (message: MessageEvent) => {
    const data = {
      events: message.data.events,
      lyra_id: lyraId,
    }
    try {
      const response = await axiosInstance.post(`/v1/zoom-event/get-zoom-event-infos`, data)
      essentialsRef.current?.contentWindow?.postMessage(
        {
          event: ESSENTIALS_IFRAME_MESSAGES.EVENT_REGISTERED_COUNT,
          success: true,
          events: response.data.events,
        },
        essentialsRef.current.contentWindow.origin,
      )
    } catch {
      essentialsRef.current?.contentWindow?.postMessage(
        {
          event: ESSENTIALS_IFRAME_MESSAGES.EVENT_REGISTERED_COUNT,
          success: false,
        },
        essentialsRef.current.contentWindow.origin,
      )
    }
  }

  const handleNavigateBetweenLibraryApps = (message: MessageEvent) => {
    const pathName = new URL(message.data.url).pathname
    let url
    if (pathName.includes('work-hub')) {
      navigate('/secure/index/work-hub')
      url = `${window.location.origin}/secure/index/work-hub`
      setContentPath('/secure/index/work-hub')
    } else {
      navigate('/secure/index/essentials')
      url = `${window.location.origin}/secure/index/essentials`
      setContentPath('/secure/index/essentials')
    }
    history.replaceState(null, '', url)
  }

  // Update the URL and browser tab title when a routeChange message is received.
  const handleRouteChange = (message: MessageEvent) => {
    let path
    if (isInitialLoadParamEnabled) {
      // The route change URL will include the initial load query param. This tries to remove it as cleanly as possible
      // so it does not get displayed to the user.
      const placeholderOrigin = 'https://placeholder.com'
      const messageUriAsURL = new URL(placeholderOrigin + String(message.data.uri))
      messageUriAsURL.searchParams.delete(ESSENTIALS_IS_INITIAL_LOAD_PARAM)
      path = String(messageUriAsURL.href).replace(placeholderOrigin, '')
    } else {
      path = message.data.uri
    }

    const newUrl = new URL(window.location.href)
    const pathName = newUrl.pathname

    let url

    // if the user is viewing ungated events hub (/essentials/events) and selects an event:
    //  update the URL to reflect the new event path (e.g. /essentials/events/some-event)
    // else:
    //  for other ungated content (e.g. /essentials/articles/some-article) we do not wish to update the URL
    if (isUngatedContent) {
      if (isUngatedEvents && message.data.uri.startsWith('/events/')) {
        const eventPath = message.data.uri.split('?')[0] // remove query params from the path
        url = `${window.location.origin}/essentials${eventPath}`
      } else {
        return
      }
    } else {
      if (pathName.includes('work-hub')) {
        url = `${window.location.origin}/secure/index/work-hub${path}`
      } else {
        url = `${window.location.origin}/secure/index/essentials${path}`
      }
    }

    history.replaceState(null, '', url)

    if (path === '/' || path === '/#0') {
      // Essentials page was forwarded into an article instead of home tab. This will need to rerender/re-navigate the original Essentials.
      if (parentAppReferrer) {
        navigate('/secure/index/essentials/')
      } else {
        document.title = `${essentialsTitle} | ${LYRA_HEALTH_NAME}`
      }
    } else {
      setTimeout(() => {
        const essentialsIframe: HTMLIFrameElement | null = document.querySelector('iframe[title="Essentials"]')
        if (essentialsIframe) {
          const essentialsTitlePieces = essentialsIframe.contentDocument?.title?.split('|')
          document.title = `${essentialsTitlePieces![0]} | ${LYRA_HEALTH_NAME}`
        }
      }, 500) // Timeout required for the iframe document.title to be updated
    }
  }

  useEffect(() => () => window.clearInterval(intervalRef.current), [])

  return (
    <>
      <div className={styles['loading-container']} style={isEssentialsLoading ? undefined : { display: 'none' }}>
        <LoadingIndicator size={45} />
      </div>
      <iframe
        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        title='Essentials'
        onLoad={onIFrameLoad}
        ref={essentialsRef}
        src={essentialsUrl}
        width='100%'
        height={iFrameHeight}
        scrolling='no'
        frameBorder='0'
      />
    </>
  )
}

function mapStateToProps(state: any) {
  return {
    lyraId: getId(state),
    userDisplayLanguage: getUserDisplayLanguageOrDefault(state),
    deviceUUID: getDeviceUUID(state),
    toolkitType: getOnboardToolkitType(state),
    isLoggedIn: getIsUserLoggedIn(state),
  }
}

const mapDispatchToProps = {
  trackEventWithObj,
  setTimeInactivityWarning,
  setTimeLastActive,
  setTriageEntryPoint,
  setShouldGoFullscreenForMixes,
}

export default connect(mapStateToProps, mapDispatchToProps)(toJS(Essentials))
